import React, { useMemo, useRef, useState } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { App, Button, Form, Input, Modal, Select, Spin, Typography } from 'antd';
import type { InputRef } from 'antd';

import { tap } from 'rxjs/operators';
import type { User } from 'v2/interfaces/access-control';
import accessControlService from 'v2/services/access-control';

type Props = {
  current?: User;
  onComplete: () => void;
  onCancel: () => void;
};

export default function FormUser({ current, onComplete, onCancel }: Props) {
  const { message } = App.useApp();
  const [form, setForm] = useState({
    name: current?.name || '',
    email: current?.email || '',
    group: current?.group_id || (undefined as number | undefined)
  });
  const [isLoadingGroups] = useObservable(() => accessControlService.isLoadingGroups$, []);
  const [groups] = useObservable(() => accessControlService.getGroups(), []);
  const [isSaving, setIsSaving] = useState(false);

  const [saveUser] = useCallbackObservable(
    data =>
      accessControlService.saveUser(data).pipe(
        tap(
          () => {
            message.success('Sucesso, usuário criado!');
            onComplete();
            setIsSaving(false);
          },
          err => {
            message.error(typeof err === 'string' ? err : 'Não foi possível criar o usuário.');
            setIsSaving(false);
          }
        )
      ),
    [message, onComplete]
  );

  const [updateUser] = useCallbackObservable(
    data =>
      accessControlService.updateUser(data).pipe(
        tap(
          () => {
            message.success('Sucesso, usuário atualizado!');
            onComplete();
            setIsSaving(false);
          },
          err => {
            message.error(typeof err === 'string' ? err : 'Não foi possível atualizar o usuário.');
            setIsSaving(false);
          }
        )
      ),
    [message, onComplete]
  );

  const nameInputRef = useRef<InputRef>(null);
  const emailInputRef = useRef<InputRef>(null);

  function handleChange(field: string, value: string | number) {
    setForm(old => ({
      ...old,
      [field]: value
    }));
  }

  const isEditing = !!current;

  function handleSubmit() {
    setIsSaving(true);

    if (!form.name) {
      nameInputRef.current?.focus();
      message.error('O nome do usuário é obrigatório.');
      setIsSaving(false);
      return;
    }

    const isEmail = /\S+@\S+\.\S+/.test(form.email);

    if (!isEmail) {
      emailInputRef.current?.focus();
      message.error('O email do usuário é inválido.');
      setIsSaving(false);
      return;
    }

    if (!form.group) {
      message.error('Selecione um grupo.');
      setIsSaving(false);
      return;
    }

    if (isEditing) {
      updateUser({
        id: current.id,
        oldGroup: current.group_id,
        ...form
      });
      return;
    }

    saveUser(form);
  }

  const groupOptions = useMemo(() => {
    return (groups || []).map(group => ({ value: group.id, label: group.name }));
  }, [groups]);

  return (
    <Modal
      open
      width={640}
      destroyOnClose
      onCancel={onCancel}
      title={isEditing ? 'Atualizar usuário' : 'Novo usuário'}
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button type='text' onClick={onCancel} disabled={isSaving}>
            Cancelar
          </Button>
          <Button type='primary' loading={isSaving || isLoadingGroups} onClick={handleSubmit}>
            {isEditing ? 'Salvar alterações' : 'Adicionar usuário'}
          </Button>
        </div>
      }
    >
      <div className='mb-5 rounded-(--uizz-antd-border-radius-lg) bg-(--uizz-antd-color-fill-tertiary) px-4 py-3'>
        <Typography.Text strong className='block'>
          {isEditing ? 'Editar vínculo do usuário' : 'Adicionar usuário a um grupo'}
        </Typography.Text>
        <Typography.Text type='secondary' className='text-sm'>
          {isEditing
            ? 'Atualize o grupo de acesso deste usuário.'
            : 'Informe os dados e selecione o grupo de permissões.'}
        </Typography.Text>
      </div>

      {isLoadingGroups && (
        <div className='flex justify-center py-8'>
          <Spin />
        </div>
      )}

      {!isLoadingGroups && (
        <Form layout='vertical' className='flex flex-col gap-4'>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <Form.Item
              label='Email'
              required
              className='mb-0'
              extra={isEditing ? <Typography.Text type='secondary'>Não é possível editar</Typography.Text> : undefined}
            >
              <Input
                ref={emailInputRef}
                name='email'
                value={form.email}
                disabled={isSaving || isEditing}
                onChange={e => handleChange('email', e.target.value)}
                placeholder='email@exemplo.com'
              />
            </Form.Item>
            <Form.Item
              label='Nome'
              required
              className='mb-0'
              extra={isEditing ? <Typography.Text type='secondary'>Não é possível editar</Typography.Text> : undefined}
            >
              <Input
                ref={nameInputRef}
                name='name'
                value={form.name}
                disabled={isSaving || isEditing}
                onChange={e => handleChange('name', e.target.value)}
                placeholder='Nome completo'
              />
            </Form.Item>
          </div>

          <Form.Item
            label='Grupo'
            required
            className='mb-0'
            help='O grupo define as permissões disponíveis para o usuário'
          >
            <Select
              value={form.group}
              disabled={isSaving}
              onChange={value => handleChange('group', value)}
              className='w-full'
              placeholder='Selecione o grupo'
              options={groupOptions}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
